import { Doc } from "@smartsuite/smartdoc";
import { MentionTarget } from "@smartsuite/smartdoc/lib/esm/extensions/mention/types";
import { Security } from "filestack-js";

import { Preset } from "./extensions";
import { getInjectedObjectJson } from "./helpers/events";
import { DocumentViewPageWidth, Platform } from "./types";

export type AiConfig = {
  enabled: boolean;
  currentCount: number;
  callsLimit: number;
  userHasOwnKey: boolean;
  isAdmin: boolean;
};

export type IncomingDoc = {
  editable?: boolean;
  isReadOnly?: boolean;
  isDarkTheme?: boolean;
  darkModeCustomBackgroundColor?: string;
  lightModeCustomBackgroundColor?: string;
  disableHeadingExpandButton?: boolean;
  ignoreHorizontalPadding?: boolean;
  initValue?: Doc;
  initYjsData?: string;
  security?: Security;
  users?: MentionTarget[];
  teams?: MentionTarget[];
  accountSlug?: string;
  applicationId?: string;
  recordId?: string;
  reportId?: string;
  fieldSlug?: string;
  memberName?: string;
  accessToken?: string;
  env?: string;
  webSocketURL?: string;
  filestackAPIKey?: string;
  platform?: Platform;
  preset?: Preset;
  pageWidth?: DocumentViewPageWidth;
  tip?: string;
  placeholder?: string;
  ai?: AiConfig;
  reportLabel?: string;
  showDocumentTitle?: boolean;
};

export type AppConfig = {
  initialDoc?: Doc;
  initialYjsData?: string;
  users: MentionTarget[];
  teams: MentionTarget[];
  isDarkTheme: boolean;
  darkModeCustomBackgroundColor: string | null;
  lightModeCustomBackgroundColor: string | null;
  disableHeadingExpandButton: boolean;
  ignoreHorizontalPadding: boolean;
  security: Security;
  editable: boolean;
  accountSlug: string | null;
  applicationId: string | null;
  recordId: string | null;
  reportId: string | null;
  fieldSlug: string | null;
  memberName: string | null;
  accessToken: string | null;
  env: string | null;
  webSocketURL: string | null;
  filestackAPIKey: string | null;
  platform: Platform;
  preset: Preset;
  pageWidth: DocumentViewPageWidth;
  tip: string;
  placeholder: string;
  aiConfig: AiConfig;
  reportLabel: string;
  showDocumentTitle: boolean;
};

export const DEFAULT_AI_CONFIG: AiConfig = {
  enabled: false,
  currentCount: 0,
  callsLimit: 200,
  userHasOwnKey: false,
  isAdmin: false,
};

export const DEFAULT_SECURITY: Security = {
  policy: "",
  signature: "",
};

export const DEFAULT_APP_CONFIG: AppConfig = {
  initialDoc: undefined,
  initialYjsData: undefined,
  users: [],
  teams: [],
  isDarkTheme: false,
  darkModeCustomBackgroundColor: null,
  lightModeCustomBackgroundColor: null,
  disableHeadingExpandButton: false,
  ignoreHorizontalPadding: false,
  security: DEFAULT_SECURITY,
  editable: true,
  accountSlug: null,
  applicationId: null,
  recordId: null,
  reportId: null,
  fieldSlug: null,
  memberName: null,
  accessToken: null,
  env: null,
  webSocketURL: null,
  filestackAPIKey: null,
  platform: null,
  preset: "full",
  pageWidth: "default",
  tip: "",
  placeholder: "",
  aiConfig: DEFAULT_AI_CONFIG,
  reportLabel: "",
  showDocumentTitle: false,
};

export const normalizeIncomingDoc = (doc: IncomingDoc): AppConfig => {
  return {
    initialDoc: doc.initValue,
    initialYjsData: doc.initYjsData,
    users: doc.users ?? [],
    teams: doc.teams ?? [],
    isDarkTheme: doc.isDarkTheme ?? false,
    darkModeCustomBackgroundColor:
      typeof doc.darkModeCustomBackgroundColor === "string"
        ? doc.darkModeCustomBackgroundColor
        : null,
    lightModeCustomBackgroundColor:
      typeof doc.lightModeCustomBackgroundColor === "string"
        ? doc.lightModeCustomBackgroundColor
        : null,
    disableHeadingExpandButton: doc.disableHeadingExpandButton === true,
    ignoreHorizontalPadding: doc.ignoreHorizontalPadding === true,
    security: doc.security ?? DEFAULT_SECURITY,
    editable: doc.editable ?? !doc.isReadOnly,
    accountSlug: doc.accountSlug ?? null,
    applicationId: doc.applicationId ?? null,
    recordId: doc.recordId ?? null,
    reportId: doc.reportId ?? null,
    fieldSlug: doc.fieldSlug ?? null,
    memberName: doc.memberName ?? null,
    accessToken: doc.accessToken ?? null,
    env: doc.env ?? null,
    webSocketURL: doc.webSocketURL ?? null,
    filestackAPIKey: doc.filestackAPIKey ?? null,
    platform: doc.platform ?? null,
    preset: doc.preset ?? "full",
    pageWidth: doc.pageWidth === "wide" ? "wide" : "default",
    tip: typeof doc.tip === "string" ? doc.tip : "",
    placeholder: typeof doc.placeholder === "string" ? doc.placeholder : "",
    reportLabel: typeof doc.reportLabel === "string" ? doc.reportLabel : "",
    showDocumentTitle: doc.showDocumentTitle === true,
    aiConfig: {
      ...DEFAULT_AI_CONFIG,
      ...doc.ai,
    },
  };
};

export const parseIncomingDoc = (value: string): IncomingDoc | null => {
  try {
    const parsed = JSON.parse(value) as unknown;

    return typeof parsed === "object" && parsed !== null ? (parsed as IncomingDoc) : null;
  } catch {
    return null;
  }
};

export const readInjectedConfig = (): AppConfig | null => {
  try {
    const injected = getInjectedObjectJson();

    if (!injected) {
      return null;
    }

    const doc = parseIncomingDoc(injected);

    if (!doc) {
      return null;
    }

    const normalized = normalizeIncomingDoc(doc);

    return normalized;
  } catch {
    return null;
  }
};
